import type { CalculatorResults, CalculatorState, Winner } from '../types'

export const DEFAULT_STATE: CalculatorState = {
  horizonYears: 15,
  purchasePrice: 400_000,
  downPaymentPct: 20,
  homeInspection: 400,
  initialRepairs: 3_000,
  movingExpenses: 2_600,
  closingCostsPct: 6,
  interestRate: 6.5,
  loanTermYears: 15,
  propertyTaxPct: 1.1,
  homeownerInsurancePct: 0.6,
  pmiPct: 0.4,
  hoaAnnual: 300,
  maintenancePct: 1,
  yardAnnual: 500,
  securityAnnual: 500,
  utilitiesAnnual: 1_490,
  majorRepairs: 0,
  appreciationPct: 3,
  agentCommissionPct: 5,
  capitalGainsTaxPct: 15,
  stagingCost: 2_000,
  monthlyRent: 2_000,
  rentersInsurancePct: 3,
  rentAppreciationPct: 3,
  investmentReturnPct: 4,
}

export function monthlyMortgagePayment(loanAmount: number, annualRate: number, termYears: number): number {
  const months = Math.max(termYears * 12, 1)
  const monthlyRate = annualRate / 12
  if (monthlyRate === 0) return loanAmount / months
  const factor = Math.pow(1 + monthlyRate, months)
  return (loanAmount * monthlyRate * factor) / (factor - 1)
}

export function computeResults(state: CalculatorState): CalculatorResults {
  const horizon = Math.max(Math.round(state.horizonYears), 1)
  const downPayment = (state.purchasePrice * state.downPaymentPct) / 100
  const loanAmount = Math.max(state.purchasePrice - downPayment, 0)
  const closingTotal = (state.purchasePrice * state.closingCostsPct) / 100
  const moveInCosts = state.homeInspection + state.initialRepairs + state.movingExpenses
  const totalUpfront = downPayment + closingTotal + moveInCosts
  const monthlyMortgage = monthlyMortgagePayment(loanAmount, state.interestRate / 100, state.loanTermYears)

  const loanMonths = Math.max(state.loanTermYears * 12, 1)
  const payMonths = Math.min(horizon * 12, loanMonths)
  const monthlyRate = state.interestRate / 100 / 12
  let totalMortgagePaid = 0
  let totalInterestPaid = 0
  let balance = loanAmount

  for (let month = 0; month < payMonths; month++) {
    const interest = balance * monthlyRate
    const principal = monthlyMortgage - interest
    totalInterestPaid += interest
    totalMortgagePaid += monthlyMortgage
    balance = Math.max(balance - principal, 0)
  }

  let propertyTaxAndInsurance = 0
  let maintenanceHoaOther = 0
  let totalPmi = 0
  let pmiBalance = loanAmount

  for (let year = 0; year < horizon; year++) {
    const homeValue = state.purchasePrice * Math.pow(1 + state.appreciationPct / 100, year)
    propertyTaxAndInsurance += (homeValue * (state.propertyTaxPct + state.homeownerInsurancePct)) / 100
    maintenanceHoaOther +=
      (homeValue * state.maintenancePct) / 100 + state.hoaAnnual + state.yardAnnual + state.securityAnnual

    // PMI cancels on LTV against the original purchase price / loan basis, not the appreciated market value.
    if (state.downPaymentPct < 20 && (state.purchasePrice - pmiBalance) / state.purchasePrice < 0.2) {
      totalPmi += (loanAmount * state.pmiPct) / 100
    }

    for (let month = 0; month < 12; month++) {
      const interest = pmiBalance * monthlyRate
      pmiBalance = Math.max(pmiBalance - (monthlyMortgage - interest), 0)
    }
  }

  const totalUtilities = state.utilitiesAnnual * horizon
  const endHomeValue = state.purchasePrice * Math.pow(1 + state.appreciationPct / 100, horizon)
  const agentCommission = (endHomeValue * state.agentCommissionPct) / 100
  const gain = Math.max(endHomeValue - state.purchasePrice, 0)
  const capitalGainsTax = (gain * state.capitalGainsTaxPct) / 100
  const totalExitCost = agentCommission + capitalGainsTax + state.stagingCost
  const netEquity = endHomeValue - Math.max(balance, 0) - totalExitCost
  const totalBuyCashOut =
    totalUpfront +
    totalMortgagePaid +
    propertyTaxAndInsurance +
    totalPmi +
    maintenanceHoaOther +
    totalUtilities +
    state.majorRepairs +
    totalExitCost
  const totalBuyNetCost = totalBuyCashOut - netEquity

  let totalRentPaid = 0
  let totalRentersInsurance = 0
  let investmentPortfolio = totalUpfront
  let currentRent = state.monthlyRent

  for (let year = 0; year < horizon; year++) {
    const annualRent = currentRent * 12
    totalRentPaid += annualRent
    totalRentersInsurance += (annualRent * state.rentersInsurancePct) / 100

    for (let month = 0; month < 12; month++) {
      investmentPortfolio *= 1 + state.investmentReturnPct / 100 / 12
    }

    currentRent *= 1 + state.rentAppreciationPct / 100
  }

  const totalRentNetCost = totalRentPaid + totalRentersInsurance - (investmentPortfolio - totalUpfront)
  const difference = totalRentNetCost - totalBuyNetCost
  const winner: Winner = difference > 0 ? 'buy' : difference < 0 ? 'rent' : 'even'
  const savingsPct = Math.min(
    Math.round((Math.abs(difference) / Math.max(totalBuyNetCost, totalRentNetCost, 1)) * 100),
    100
  )

  return {
    downPayment,
    loanAmount,
    closingTotal,
    moveInCosts,
    totalUpfront,
    monthlyMortgage,
    totalMortgagePaid,
    totalInterestPaid,
    propertyTaxAndInsurance,
    totalPmi,
    maintenanceHoaOther,
    totalUtilities,
    endHomeValue,
    agentCommission,
    capitalGainsTax,
    totalExitCost,
    netEquity,
    totalBuyCashOut,
    totalBuyNetCost,
    totalRentPaid,
    totalRentersInsurance,
    investmentPortfolio,
    totalRentNetCost,
    difference,
    savingsPct,
    winner,
  }
}
